/**
 * MarketplaceListingDetail - Full listing detail page with purchase functionality
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db, getArtifactProjectId } from '../../firebaseInit';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import Card from '../shared/Card';
import Button from '../shared/Button';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';
import { getCategoryById, formatRating, getStatusBadgeStyles } from './MarketplaceCategories';
import Modal from '../shared/Modal';
import MarketplaceReportModal from './MarketplaceReportModal';
import MarketplacePurchaseModal from './MarketplacePurchaseModal';

// Full script viewer for admin — shows every node with script text, coaching, and responses
const FullScriptViewer = ({ template }) => {
  if (!template) return <p className="text-sm text-gray-500">Template content unavailable.</p>;

  const rawGraph = template.graph;
  const nodes = rawGraph?.nodes && typeof rawGraph.nodes === 'object'
    ? rawGraph.nodes
    : rawGraph;

  if (!nodes || typeof nodes !== 'object') {
    return <p className="text-sm text-gray-500">No script nodes found in this template.</p>;
  }

  const nodeEntries = Object.entries(nodes).filter(([k]) => k !== 'ui' && k !== 'nodes');

  if (nodeEntries.length === 0) {
    return <p className="text-sm text-gray-500">This script has no nodes.</p>;
  }

  return (
    <div className="space-y-3">
      {nodeEntries.map(([key, node]) => (
        <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200 flex items-center gap-2">
            <span className="text-xs font-mono text-gray-400">{key}</span>
          </div>
          <div className="p-3 space-y-2">
            {node?.script && (
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{node.script}</p>
            )}
            {node?.coaching && (
              <div className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1.5">
                <p className="text-xs text-yellow-700">
                  <span className="font-semibold">Coaching tip:</span> {node.coaching}
                </p>
              </div>
            )}
            {node?.responses && Object.keys(node.responses).length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-400 mb-1">Responses</p>
                <div className="space-y-1">
                  {Object.entries(node.responses).map(([rKey, resp]) => (
                    <div key={rKey} className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="text-gray-400">→</span>
                      <span className="font-medium">{resp?.label || rKey}</span>
                      {resp?.isEndNode ? (
                        <span className="text-red-400 font-medium">END</span>
                      ) : resp?.nextNode ? (
                        <span className="text-gray-400 font-mono">→ {resp.nextNode}</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const MarketplaceListingDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const listingId = location.pathname.split('/marketplace/listing/')[1];
  const { isAdmin, userTeam, effectiveTeamId } = useAuth();
  const { refreshTemplates } = useData();
  const {
    getListingById,
    getListingReviews,
    hasPurchased,
    isOwner,
    categories,
    hasMarketplaceAccess,
    initializeMarketplace,
    approveListing,
    rejectListing,
    deleteListing,
    deleteReview,
    loadMyPurchases,
  } = useMarketplace();

  const targetTeamId = effectiveTeamId || userTeam;

  useEffect(() => {
    initializeMarketplace();
  }, [initializeMarketplace]);

  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteListingModal, setShowDeleteListingModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminProcessing, setAdminProcessing] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [freeAcquiring, setFreeAcquiring] = useState(false);
  const [freeAcquired, setFreeAcquired] = useState(false);
  const [freeAcquireError, setFreeAcquireError] = useState(null);
  const [deletingReviewId, setDeletingReviewId] = useState(null);

  // Admin: full template for complete script viewer
  const [fullTemplate, setFullTemplate] = useState(null);
  const [loadingFullTemplate, setLoadingFullTemplate] = useState(false);

  // Load listing data
  useEffect(() => {
    const loadData = async () => {
      if (!listingId || !hasMarketplaceAccess) return;

      setLoading(true);
      setError(null);

      try {
        const listingData = await getListingById(listingId);
        if (!listingData) {
          setError('Listing not found');
        } else {
          setListing(listingData);
          const reviewsData = await getListingReviews(listingId);
          setReviews(reviewsData);
        }
      } catch (err) {
        console.error('Error loading listing:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [listingId, hasMarketplaceAccess, getListingById, getListingReviews, refreshKey]);

  // Admin: load full template after listing loads
  useEffect(() => {
    if (!isAdmin || !listing) return;

    const loadFullTemplate = async () => {
      setLoadingFullTemplate(true);
      try {
        const appId = getArtifactProjectId();
        const teamId = listing.teamId;
        const templateId = listing.templateId;

        if (!teamId || !templateId) {
          setFullTemplate(null);
          return;
        }

        const templateRef = doc(db, `artifacts/${appId}/teams/${teamId}/templates/${templateId}`);
        const templateSnap = await getDoc(templateRef);

        if (templateSnap.exists()) {
          setFullTemplate({ id: templateSnap.id, ...templateSnap.data() });
        } else {
          setFullTemplate(null);
        }
      } catch (err) {
        console.warn('Could not load full template for admin:', err.message);
        setFullTemplate(null);
      } finally {
        setLoadingFullTemplate(false);
      }
    };

    loadFullTemplate();
  }, [isAdmin, listing]);

  // Access check
  if (!hasMarketplaceAccess) {
    return (
      <div className="p-4">
        <EmptyState
          icon="🔒"
          title="Access Restricted"
          description="The Script Marketplace is available to Team Leads and Admins only."
          action={
            <Button variant="primary" onClick={() => navigate('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 flex justify-center">
        <LoadingSpinner size="large" message="Loading script details..." />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="p-4">
        <EmptyState
          icon="📋"
          title="Script Not Found"
          description={error || "This script doesn't exist or has been removed."}
          action={
            <Button variant="primary" onClick={() => navigate('/marketplace')}>
              Browse Marketplace
            </Button>
          }
        />
      </div>
    );
  }

  const handleFreeAcquire = async () => {
    if (freeAcquiring || freeAcquired) return;
    setFreeAcquiring(true);
    setFreeAcquireError(null);
    try {
      const acquireScript = httpsCallable(functions, 'purchaseFreeScript');
      const result = await acquireScript({
        listingId: listing.id,
        buyerTeamId: targetTeamId,
      });
      if (!result.data?.success) {
        throw new Error('Failed to acquire script.');
      }
      await Promise.all([loadMyPurchases(), refreshTemplates()]);
      setFreeAcquired(true);
      setRefreshKey(k => k + 1);
    } catch (err) {
      const message = err.code === 'already-exists'
        ? 'You have already acquired this script.'
        : (err.message || 'Failed to acquire script. Please try again.');
      setFreeAcquireError(message);
    } finally {
      setFreeAcquiring(false);
    }
  };

  const handleAdminApprove = async () => {
    setAdminProcessing(true);
    setAdminError(null);
    try {
      await approveListing(listing.id);
      const updatedListing = await getListingById(listingId);
      setListing(updatedListing);
    } catch (err) {
      console.error('Error approving listing:', err);
      setAdminError('Failed to approve listing. Please try again.');
    } finally {
      setAdminProcessing(false);
    }
  };

  const handleAdminReject = async () => {
    if (!rejectionReason.trim()) return;
    setAdminProcessing(true);
    setAdminError(null);
    try {
      await rejectListing(listing.id, rejectionReason);
      setShowRejectModal(false);
      setRejectionReason('');
      const updatedListing = await getListingById(listingId);
      setListing(updatedListing);
    } catch (err) {
      console.error('Error rejecting listing:', err);
      setAdminError('Failed to reject listing. Please try again.');
    } finally {
      setAdminProcessing(false);
    }
  };

  const handleAdminDeleteListing = async () => {
    setAdminProcessing(true);
    setAdminError(null);
    try {
      await deleteListing(listing.id);
      setShowDeleteListingModal(false);
      navigate('/marketplace/admin');
    } catch (err) {
      console.error('Error deleting listing:', err);
      setAdminError('Failed to delete listing. Please try again.');
      setAdminProcessing(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    setDeletingReviewId(reviewId);
    try {
      await deleteReview(reviewId, listing.id);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      // Refresh listing to get updated rating/reviewCount
      const updatedListing = await getListingById(listingId);
      if (updatedListing) setListing(updatedListing);
    } catch (err) {
      console.error('Error deleting review:', err);
    } finally {
      setDeletingReviewId(null);
    }
  };

  const category = getCategoryById(listing.category, categories);
  const isPurchased = hasPurchased(listing.id);
  const isOwnListing = isOwner(listing);
  const statusBadge = getStatusBadgeStyles(listing.status);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate(isAdmin ? '/marketplace/admin' : '/marketplace')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        ← {isAdmin ? 'Back to Admin Queue' : 'Back to Marketplace'}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Header card */}
          <Card padding="md">
            {listing.status !== 'active' && (
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-4 ${statusBadge.bg} ${statusBadge.text}`}>
                {statusBadge.label}
              </div>
            )}

            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full mb-3">
              <span>{category.icon}</span>
              <span>{category.name}</span>
            </span>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {listing.title}
            </h1>

            <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
              <span className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                {listing.sellerDisplayName?.charAt(0)?.toUpperCase() || '?'}
              </span>
              <span>Created by {listing.sellerDisplayName}</span>
              {listing.featured && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                  ⭐ Featured
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
              <div className="flex items-center gap-1">
                <span className="text-yellow-500">★</span>
                <span>{formatRating(listing.rating, listing.reviewCount)}</span>
              </div>
              <div className="flex items-center gap-1">
                <span>📥</span>
                <span>{listing.totalSales} sales</span>
              </div>
              <div className="flex items-center gap-1">
                <span>👁️</span>
                <span>{listing.viewCount} views</span>
              </div>
              <div className="flex items-center gap-1">
                <span>📋</span>
                <span>{listing.nodeCount} nodes</span>
              </div>
              {listing.hasCoachingTips && (
                <div className="flex items-center gap-1 text-blue-600">
                  <span>💡</span>
                  <span>Includes coaching tips</span>
                </div>
              )}
            </div>

            <div className="prose prose-sm max-w-none">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{listing.description}</p>
            </div>

            {listing.tags && listing.tags.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex flex-wrap gap-2">
                  {listing.tags.map((tag, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Script content — full viewer for admin, preview for everyone else */}
          {isAdmin ? (
            <Card padding="md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Full Script Content</h3>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium">Admin View</span>
              </div>
              {loadingFullTemplate ? (
                <div className="flex justify-center py-6">
                  <LoadingSpinner size="medium" message="Loading script content..." />
                </div>
              ) : fullTemplate ? (
                <FullScriptViewer template={fullTemplate} />
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    Full template could not be loaded. Showing preview nodes below.
                  </p>
                  {listing.previewNodes && listing.previewNodes.length > 0 && listing.previewNodes.map((node, index) => (
                    <div key={node.key || index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {node.question || 'Script node'}
                      </div>
                      {node.responseCount > 0 && (
                        <div className="text-xs text-gray-500">
                          {node.responseCount} response{node.responseCount !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ) : listing.previewNodes && listing.previewNodes.length > 0 ? (
            <Card padding="md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Script Preview</h3>
              <div className="space-y-3">
                {listing.previewNodes.map((node, index) => (
                  <div key={node.key || index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {node.question || 'Script node'}
                    </div>
                    {node.responseCount > 0 && (
                      <div className="text-xs text-gray-500">
                        {node.responseCount} response{node.responseCount !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                ))}
                {listing.nodeCount > listing.previewNodes.length && (
                  <p className="text-sm text-gray-500 italic text-center py-2">
                    + {listing.nodeCount - listing.previewNodes.length} more nodes (purchase to view full script)
                  </p>
                )}
              </div>
            </Card>
          ) : null}

          {/* Reviews */}
          <Card padding="md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Reviews ({reviews.length})
            </h3>

            {reviews.length === 0 ? (
              <p className="text-gray-500 text-sm">No reviews yet. Be the first to review this script!</p>
            ) : (
              <div className="space-y-4">
                {reviews.map(review => (
                  <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{review.reviewerDisplayName}</span>
                          {review.verifiedPurchase && (
                            <span className="text-xs text-green-600">✓ Verified Purchase</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-sm">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span
                              key={star}
                              className={star <= review.rating ? 'text-yellow-500' : 'text-gray-300'}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">
                          {review.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                        </span>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteReview(review.id)}
                            disabled={deletingReviewId === review.id}
                            className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50 px-1.5 py-0.5 rounded hover:bg-red-50 transition-colors"
                            aria-label="Delete review"
                          >
                            {deletingReviewId === review.id ? 'Deleting...' : '🗑 Delete'}
                          </button>
                        )}
                      </div>
                    </div>
                    {review.title && (
                      <h4 className="font-medium text-gray-900 text-sm mb-1">{review.title}</h4>
                    )}
                    {review.body ? (
                      <p className="text-gray-600 text-sm">{review.body}</p>
                    ) : (
                      <p className="text-gray-400 text-xs italic">Rating only — no written review</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-4">
            {/* Purchase card */}
            <Card padding="md">
              <div className="text-center mb-4">
                {listing.isFree ? (
                  <div className="text-3xl font-bold text-green-600">FREE</div>
                ) : (
                  <div className="text-3xl font-bold text-gray-900">${listing.price}</div>
                )}
                <p className="text-sm text-gray-500 mt-1">One-time purchase</p>
              </div>

              <div className="space-y-2">
                {isOwnListing ? (
                  <>
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={() => navigate(`/marketplace/sell?edit=${listing.id}`)}
                    >
                      Edit Listing
                    </Button>
                    <p className="text-xs text-gray-500 text-center">This is your listing</p>
                  </>
                ) : (isPurchased || freeAcquired) ? (
                  <>
                    <Button variant="secondary" className="w-full" disabled>
                      ✓ Added to Your Scripts
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate('/marketplace/purchases')}
                    >
                      View in My Purchases
                    </Button>
                  </>
                ) : listing.isFree ? (
                  <>
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={handleFreeAcquire}
                      disabled={freeAcquiring}
                    >
                      {freeAcquiring ? 'Adding to Your Scripts...' : 'Get This Script'}
                    </Button>
                    {freeAcquireError && (
                      <p className="text-sm text-red-600 text-center mt-2">{freeAcquireError}</p>
                    )}
                  </>
                ) : (
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={() => setShowPurchaseModal(true)}
                  >
                    {`Buy for $${listing.price}`}
                  </Button>
                )}
              </div>

              {!isOwnListing && !isPurchased && !listing.isFree && (
                <p className="text-xs text-gray-400 text-center mt-3">
                  Seller receives 80% • Secure payment via Stripe
                </p>
              )}
            </Card>

            {/* Report button */}
            {!isOwnListing && !isAdmin && (
              <Card padding="sm">
                <button
                  onClick={() => setShowReportModal(true)}
                  className="w-full text-sm text-gray-500 hover:text-red-600 flex items-center justify-center gap-2"
                >
                  🚩 Report this listing
                </button>
              </Card>
            )}

            {/* Admin actions */}
            {isAdmin && (
              <Card padding="md">
                <h4 className="font-medium text-gray-900 mb-3">Admin Actions</h4>
                {adminError && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    {adminError}
                  </div>
                )}

                <div className="space-y-2">
                  {listing.status === 'pending_review' && (
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full"
                        onClick={handleAdminApprove}
                        disabled={adminProcessing}
                      >
                        {adminProcessing ? 'Processing...' : '✓ Approve Listing'}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        className="w-full"
                        onClick={() => setShowRejectModal(true)}
                        disabled={adminProcessing}
                      >
                        ✕ Reject Listing
                      </Button>
                    </>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => setShowDeleteListingModal(true)}
                    disabled={adminProcessing}
                  >
                    🗑 Remove Listing
                  </Button>
                </div>

                {listing.failedChecks && listing.failedChecks.length > 0 && (
                  <div className="mt-3 p-2 bg-yellow-50 rounded text-xs">
                    <p className="font-medium text-yellow-800">Failed checks:</p>
                    <ul className="text-yellow-700 list-disc list-inside">
                      {listing.failedChecks.map(check => (
                        <li key={check}>{check}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showReportModal && (
        <MarketplaceReportModal
          listingId={listing.id}
          listingTitle={listing.title}
          onClose={() => setShowReportModal(false)}
        />
      )}

      {showPurchaseModal && (
        <MarketplacePurchaseModal
          listing={listing}
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={() => {
            setShowPurchaseModal(false);
            setRefreshKey(k => k + 1);
          }}
        />
      )}

      {/* Admin: Reject Modal */}
      {showRejectModal && (
        <Modal
          isOpen
          onClose={() => {
            setShowRejectModal(false);
            setRejectionReason('');
          }}
          title="Reject Listing"
        >
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">
                Rejecting: <strong>{listing.title}</strong>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this listing is being rejected. This will be shown to the seller."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {adminError && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {adminError}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                disabled={adminProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleAdminReject}
                disabled={!rejectionReason.trim() || adminProcessing}
              >
                {adminProcessing ? 'Rejecting...' : 'Reject Listing'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Admin: Delete Listing Confirmation Modal */}
      {showDeleteListingModal && (
        <Modal
          isOpen
          onClose={() => setShowDeleteListingModal(false)}
          title="Delete Listing"
        >
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
              <p className="text-sm text-red-800 font-medium">Are you sure you want to remove this listing?</p>
              <p className="text-sm text-red-700 mt-1">
                Removing <strong>"{listing.title}"</strong> will take it off the marketplace.
                Existing purchases will not be affected.
              </p>
            </div>

            {adminError && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {adminError}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteListingModal(false)}
                disabled={adminProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleAdminDeleteListing}
                disabled={adminProcessing}
              >
                {adminProcessing ? 'Removing...' : 'Remove Listing'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MarketplaceListingDetail;
